import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  FileText,
  Search,
  Eye,
  LogOut,
  Settings,
  Info,
  Award,
  Calendar,
  Loader2,
  X,
  TrendingUp,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/common/DataTable';
import { useReferralUsersListing, useDownlineStats } from '@/hooks/useReferral';
import { ReferralUser } from '@/api/referral';
import { formatDate } from '@/utils';

// Helper to format currency natively in Indian Style
const formatCurrency = (val: number) => {
  return `₹${val.toLocaleString('en-IN')}`;
};

interface DownlineModalProps {
  userId: number;
  userName: string;
  onClose: () => void;
}

function DownlineReportModal({ userId, userName, onClose }: DownlineModalProps) {
  const { data: stats, isLoading, isError } = useDownlineStats(userId);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-2xl bg-background border border-border rounded-xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-foreground">DownLine Report</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Showing downline referral network stats for {userName}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-[#6F239B]" />
              <p className="text-sm font-medium text-muted-foreground">
                Fetching downline stats...
              </p>
            </div>
          ) : isError || !stats ? (
            <div className="text-center py-8 text-destructive font-semibold">
              Failed to load downline statistics. Please try again.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {/* Card 1 */}
              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl p-4 text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Downline Count
                </p>
                <p className="text-2xl font-black text-[#00B4D8] dark:text-[#00B4D8] mt-2">
                  {stats.downlineCount}
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl p-4 text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Total Registered (L1)
                </p>
                <p className="text-2xl font-black text-[#00B4D8] dark:text-[#00B4D8] mt-2">
                  {stats.directSentL1}
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl p-4 text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Purchase Count (L1)
                </p>
                <p className="text-2xl font-black text-[#00B4D8] dark:text-[#00B4D8] mt-2">
                  {stats.purchaseCountL1}
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl p-4 text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Purchase Amount (L1)
                </p>
                <p className="text-xl font-black text-[#00B4D8] dark:text-[#00B4D8] mt-2">
                  {formatCurrency(stats.purchaseAmountL1)}{' '}
                  <span className="text-xs font-bold text-muted-foreground/80">INR</span>
                </p>
              </div>

              {/* Card 5 */}
              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl p-4 text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Purchase Count (All Level)
                </p>
                <p className="text-2xl font-black text-[#00B4D8] dark:text-[#00B4D8] mt-2">
                  {stats.purchaseCountAll}
                </p>
              </div>

              {/* Card 6 */}
              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl p-4 text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Purchase Amount (All Level)
                </p>
                <p className="text-xl font-black text-[#00B4D8] dark:text-[#00B4D8] mt-2">
                  {formatCurrency(stats.purchaseAmountAll)}{' '}
                  <span className="text-xs font-bold text-muted-foreground/80">INR</span>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-2 border-t px-6 py-4 bg-muted/20">
          <Button variant="outline" onClick={onClose} className="px-6 h-9 font-bold">
            Cancel
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function ReferralReportPage() {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [referralFilter, setReferralFilter] = useState('all');

  // Pagination states
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(7);

  // Selected user downline detail
  const [activeModalUser, setActiveModalUser] = useState<{ id: number; name: string } | null>(null);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Reset pagination index on filters change
  useEffect(() => {
    setPageIndex(0);
  }, [searchQuery, fromDate, toDate, referralFilter]);

  // Query params mapper
  const listingParams = {
    skip: pageIndex * pageSize,
    take: pageSize,
    search: searchQuery || undefined,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    referralFilter: referralFilter !== 'all' ? referralFilter : undefined,
  };

  const { data, isLoading } = useReferralUsersListing(listingParams);
  const users = data?.data ?? [];
  const totalCount = data?.count ?? 0;
  const pageCount = Math.ceil(totalCount / pageSize);

  const columns: ColumnDef<ReferralUser>[] = [
    {
      accessorKey: 'index',
      header: 'Sr. No.',
      cell: ({ row }) => {
        const index = row.index + 1 + pageIndex * pageSize;
        return <span className="font-medium text-muted-foreground">{index}</span>;
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const u = row.original;
        const name = u.name || `${u.firstname} ${u.lastname}`;
        return <div className="font-semibold text-foreground text-sm">{name}</div>;
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => {
        const email = row.original.email || '';
        const truncateCenter = (str: string, maxLen = 18) => {
          if (!str || str.length <= maxLen) return str;
          const start = Math.ceil((maxLen - 3) / 2);
          const end = Math.floor((maxLen - 3) / 2);
          return `${str.slice(0, start)}...${str.slice(-end)}`;
        };
        return (
          <span className="text-sm text-black  inline-block" title={email}>
            {truncateCenter(email, 18)}
          </span>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Date',
      cell: ({ row }) => {
        return <span className="text-sm">{formatDate(new Date(row.original.createdAt))}</span>;
      },
    },
    {
      accessorKey: 'sponsorId',
      header: 'Referred ID',
      cell: ({ row }) => {
        const sp = row.original.sponsorId;
        return sp ? (
          <span className="font-medium text-sm text-[#00B4D8] dark:text-[#00B4D8]">{sp} </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      accessorKey: 'purchase',
      header: 'Purchase',
      cell: ({ row }) => {
        const purchase = row.original.purchase;
        return <span className="font-semibold text-sm">₹{purchase.toLocaleString()}</span>;
      },
    },
    {
      accessorKey: 'joinViaReferral',
      header: 'Join via Referral',
      cell: ({ row }) => {
        const isYes = row.original.joinViaReferral === 'YES';
        return (
          <span
            className={`font-medium text-sm ${isYes ? 'text-[#00B4D8]' : 'text-muted-foreground'}`}
          >
            {row.original.joinViaReferral}
          </span>
        );
      },
    },
    {
      accessorKey: 'downline',
      header: 'Downline',
      cell: ({ row }) => {
        const u = row.original;
        return (
          <div className="flex items-center gap-1.5 font-semibold text-sm text-muted-foreground">
            {/* <span>[ </span> */}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 p-0 text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50 dark:hover:bg-cyan-950/20 rounded"
              onClick={() =>
                setActiveModalUser({ id: u.id, name: u.name || `${u.firstname} ${u.lastname}` })
              }
            >
              <Eye className="h-4 w-4" />
            </Button>
            {/* <span> ] / ₹{u.downlinePurchaseAmount.toLocaleString()}</span> */}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b pb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground">Referral Report</h2>
          <p className="text-muted-foreground text-sm">
            View sponsor, direct purchase, levels, and downline purchase amounts over time.
          </p>
        </div>
      </div>

      {/* Main Table */}
      <DataTable
        data={users}
        columns={columns}
        searchPlaceholder="Search by  name..."
        enableSearch={true}
        enableFilters={false}
        filterElement={
          <div className="flex flex-wrap items-center gap-3">
            <Select value={referralFilter} onValueChange={(val) => setReferralFilter(val)}>
              <SelectTrigger className="w-[160px] h-9">
                <SelectValue placeholder="All Referrals" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Referrals</SelectItem>
                <SelectItem value="referral">Join via Ref (YES)</SelectItem>
                <SelectItem value="no_referral">Join via Ref (NO)</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">From:</span>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-9 w-[130px] text-xs font-medium"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">To:</span>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-9 w-[130px] text-xs font-medium"
              />
            </div>
          </div>
        }
        pageSize={pageSize}
        pageCount={pageCount}
        pageIndex={pageIndex}
        onPageIndexChange={setPageIndex}
        onPageSizeChange={setPageSize}
        searchQuery={searchInput}
        onSearchQueryChange={setSearchInput}
        enableColumnVisibility={false}
      />

      {/* Downline Report stats Modal */}
      {activeModalUser !== null && (
        <DownlineReportModal
          userId={activeModalUser.id}
          userName={activeModalUser.name}
          onClose={() => setActiveModalUser(null)}
        />
      )}
    </div>
  );
}
